import { useSidePanelClose } from "PFComponents/side_panel/use_side_panel_close";
import { useCallback } from "react";

import { PanelKey } from "./side_panel_store.types";
import { useSidePanelsActions } from "./use_side_panels_actions";
import { useSidePanelsStore } from "./use_side_panels_store";

type PanelActions<T extends object = any> = {
  open: (data: T, stack?: boolean, persist?: boolean, disableEntranceAnimation?: boolean) => void;
  close: () => void;
  setData: (newData: T | ((data: T) => T)) => void;
  setIsClosing: (isClosing: boolean) => void;
  closeAll: () => void;
};

export const useSidePanelActions = <T extends object>(key: PanelKey): PanelActions<T> => {
  const {
    setPanelData,
    openPanel,
    closePanel,
    setPanelIsClosing,
    closeAllPanels: closeAll
  } = useSidePanelsActions<T>();

  const setData = useCallback((newData: T) => setPanelData(key, newData), [setPanelData, key]);
  const open = useCallback(
    (data: T, stack = false, persist = false, disableEntranceAnimation = false) =>
      openPanel(key, data, stack, persist, disableEntranceAnimation),
    [openPanel, key]
  );
  const close = useCallback(() => closePanel(key), [closePanel, key]);
  const setIsClosing = useCallback(
    (isClosing: boolean) => setPanelIsClosing(key, isClosing),
    [setPanelIsClosing, key]
  );

  return { open, close, closeAll, setData, setIsClosing };
};

export const useSidePanelCloseExternal = (): ((key: PanelKey) => void) => {
  const { closePanel, setPanelIsClosing } = useSidePanelsStore((state) => state.actions);

  const { onSidePanelClose } = useSidePanelClose({
    onClose: closePanel,
    onCloseStart: (key: PanelKey) => setPanelIsClosing(key, true),
    onCloseEnd: (key: PanelKey) => setPanelIsClosing(key, false)
  });

  return onSidePanelClose;
};
